import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { formatError } from '../utils/errorHandler';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  DEFAULT_WORK_HOUR_TEMPLATE,
  WEEKDAY_OPTIONS,
  buildSavePayload,
  mapApiToForm,
  slugifyHourType
} from '../utils/workHourRulesForm';
import './WorkHourRules.css';

const WorkHourRules = () => {
  const { t } = useTranslation(['workHourRules', 'common']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(mapApiToForm(DEFAULT_WORK_HOUR_TEMPLATE));

  const weekdayLabels = useMemo(() => ({
    0: t('workHourRules:weekdaySun'),
    1: t('workHourRules:weekdayMon'),
    2: t('workHourRules:weekdayTue'),
    3: t('workHourRules:weekdayWed'),
    4: t('workHourRules:weekdayThu'),
    5: t('workHourRules:weekdayFri'),
    6: t('workHourRules:weekdaySat')
  }), [t]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/work-hour-rules');
        setForm(mapApiToForm(response.data.data));
        setError('');
      } catch (loadError) {
        setError(formatError(loadError, t('workHourRules:failedLoad')));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [t]);

  const updateHourType = (index, field, value) => {
    setForm((prev) => {
      const current = prev.hourTypes[index];
      const previousSlug = current?.slug;
      const hourTypes = prev.hourTypes.map((type, typeIndex) => {
        if (typeIndex !== index) {
          return type;
        }
        const next = { ...type, [field]: value };
        if (field === 'label' && !type.slugLocked) {
          next.slug = slugifyHourType(value);
        }
        return next;
      });
      const nextSlug = hourTypes[index]?.slug;
      const rules = previousSlug && nextSlug && previousSlug !== nextSlug
        ? prev.rules.map((rule) => (
          rule.hourTypeSlug === previousSlug
            ? { ...rule, hourTypeSlug: nextSlug }
            : rule
        ))
        : prev.rules;
      return { ...prev, hourTypes, rules };
    });
  };

  const addHourType = () => {
    setForm((prev) => {
      const label = t('workHourRules:hourTypeDefaultLabel', { number: prev.hourTypes.length + 1 });
      return {
        ...prev,
        hourTypes: [
          ...prev.hourTypes,
          { slug: slugifyHourType(label), label, sortOrder: prev.hourTypes.length, slugLocked: false }
        ]
      };
    });
  };

  const removeHourType = (index) => {
    setForm((prev) => {
      const removed = prev.hourTypes[index];
      if (!removed) {
        return prev;
      }
      return {
        ...prev,
        hourTypes: prev.hourTypes.filter((_, typeIndex) => typeIndex !== index),
        rules: prev.rules.filter((rule) => rule.hourTypeSlug !== removed.slug)
      };
    });
  };

  const updateRule = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      rules: prev.rules.map((rule, ruleIndex) => (
        ruleIndex === index ? { ...rule, [field]: value } : rule
      ))
    }));
  };

  const toggleRuleWeekday = (index, weekday) => {
    setForm((prev) => ({
      ...prev,
      rules: prev.rules.map((rule, ruleIndex) => {
        if (ruleIndex !== index) {
          return rule;
        }
        const weekdays = rule.weekdays.includes(weekday)
          ? rule.weekdays.filter((day) => day !== weekday)
          : [...rule.weekdays, weekday];
        return { ...rule, weekdays };
      })
    }));
  };

  const addRule = () => {
    setForm((prev) => ({
      ...prev,
      rules: [
        ...prev.rules,
        {
          hourTypeSlug: prev.hourTypes[0]?.slug || 'ordinary',
          weekdays: [1, 2, 3, 4, 5],
          startTime: '09:00',
          endTime: '17:00',
          sortOrder: prev.rules.length
        }
      ]
    }));
  };

  const removeRule = (index) => {
    setForm((prev) => ({
      ...prev,
      rules: prev.rules.filter((_, ruleIndex) => ruleIndex !== index)
    }));
  };

  const handleReset = () => {
    setForm(mapApiToForm(DEFAULT_WORK_HOUR_TEMPLATE));
    setSuccess('');
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setSuccess('');
      const response = await axios.put('/api/work-hour-rules', buildSavePayload(form));
      setForm(mapApiToForm(response.data.data));
      setSuccess(t('workHourRules:savedSuccess'));
      setError('');
    } catch (saveError) {
      setError(formatError(saveError, t('workHourRules:failedSave')));
      setSuccess('');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="work-hour-rules-page">
      <div className="container container--wide main-content">
        <div className="page-header work-hour-rules-header">
          <div>
            <h1>{t('workHourRules:pageTitle')}</h1>
            <p className="work-hour-rules-subtitle">
              {t('workHourRules:subtitle')}
            </p>
          </div>
          <Link to="/time-report" className="btn btn-secondary">
            {t('workHourRules:backToTimeReport')}
          </Link>
        </div>

        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}

        <form className="work-hour-rules-form" onSubmit={handleSubmit}>
          <section className="card work-hour-rules-section">
            <h2>{t('workHourRules:timezoneSection')}</h2>
            <p className="work-hour-rules-help">
              {t('workHourRules:timezoneHelp')}
            </p>
            <label htmlFor="work-hour-timezone">{t('workHourRules:timezoneLabel')}</label>
            <input
              id="work-hour-timezone"
              type="text"
              value={form.timezone}
              onChange={(event) => setForm((prev) => ({ ...prev, timezone: event.target.value }))}
              required
            />
          </section>

          <section className="card work-hour-rules-section">
            <div className="work-hour-rules-section-header">
              <h2>{t('workHourRules:hourTypesSection')}</h2>
              <button type="button" className="btn btn-secondary btn-sm" onClick={addHourType}>
                {t('workHourRules:addHourType')}
              </button>
            </div>
            <p className="work-hour-rules-help">
              {t('workHourRules:hourTypesHelp')}
            </p>
            <div className="work-hour-types-list">
              {form.hourTypes.map((type, index) => (
                <div key={`${type.slug}-${index}`} className="work-hour-type-row">
                  <label>
                    {t('workHourRules:labelColumn')}
                    <input
                      type="text"
                      value={type.label}
                      onChange={(event) => updateHourType(index, 'label', event.target.value)}
                      required
                    />
                  </label>
                  <label>
                    {t('workHourRules:keyColumn')}
                    <input type="text" value={type.slug} readOnly />
                  </label>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => removeHourType(index)}
                    disabled={form.hourTypes.length <= 1}
                  >
                    {t('workHourRules:remove')}
                  </button>
                </div>
              ))}
            </div>
          </section>

          <section className="card work-hour-rules-section">
            <div className="work-hour-rules-section-header">
              <h2>{t('workHourRules:timeSlotSection')}</h2>
              <button type="button" className="btn btn-secondary btn-sm" onClick={addRule}>
                {t('workHourRules:addRule')}
              </button>
            </div>
            <div className="work-hour-rules-table-wrap">
              <table className="work-hour-rules-table">
                <thead>
                  <tr>
                    <th>{t('workHourRules:hourTypeColumn')}</th>
                    <th>{t('workHourRules:weekdaysColumn')}</th>
                    <th>{t('workHourRules:startColumn')}</th>
                    <th>{t('workHourRules:endColumn')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {form.rules.map((rule, index) => (
                    <tr key={`rule-${index}`}>
                      <td>
                        <select
                          value={rule.hourTypeSlug}
                          onChange={(event) => updateRule(index, 'hourTypeSlug', event.target.value)}
                        >
                          {form.hourTypes.map((type) => (
                            <option key={type.slug} value={type.slug}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <div className="work-hour-weekdays">
                          {WEEKDAY_OPTIONS.map((day) => (
                            <label key={day.value} className="work-hour-weekday">
                              <input
                                type="checkbox"
                                checked={rule.weekdays.includes(day.value)}
                                onChange={() => toggleRuleWeekday(index, day.value)}
                              />
                              <span>{weekdayLabels[day.value]}</span>
                            </label>
                          ))}
                        </div>
                      </td>
                      <td>
                        <input
                          type="time"
                          value={rule.startTime}
                          onChange={(event) => updateRule(index, 'startTime', event.target.value)}
                          required
                        />
                      </td>
                      <td>
                        <input
                          type="time"
                          value={rule.endTime}
                          onChange={(event) => updateRule(index, 'endTime', event.target.value)}
                          required
                        />
                      </td>
                      <td>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeRule(index)}>
                          {t('workHourRules:remove')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <div className="work-hour-rules-actions">
            <button type="button" className="btn btn-secondary" onClick={handleReset} disabled={saving}>
              {t('workHourRules:resetTemplate')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('workHourRules:saving') : t('workHourRules:saveRules')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkHourRules;
